/* eslint-disable canonical/filename-match-exported -- FIXME: Fix this ESLint violation! */
import { makeAutoObservable } from 'mobx'

import Collection from '@src/service/collections/Collection'
import type { MemberModel, PhoneNumberModel } from '@src/service/model'
import { ParticipantModel } from '@src/service/model'
import type { ContactModel } from '@src/service/model/contact/ContactModel'
import type { ContactSuggestionModel } from '@src/service/model/contact/ContactSuggestionModel'

import type Service from '.'

export default class ParticipantStore {
  readonly collection = new Collection<ParticipantModel>({
    idKey: 'phoneNumber',
    bindElements: true,
  })

  constructor(private root: Service) {
    makeAutoObservable(this, {})
  }

  get(phoneNumber: string): ParticipantModel | null {
    return this.collection.get(phoneNumber)
  }

  loadByNumber(phoneNumber: string) {
    if (this.root.flags.flags.webMissingContactsInConversations) {
      // Fetch contact on-demand from the API.
      // The dedup cache in fetchContactByNumberFromApi prevents redundant calls.
      this.root.contact.fetchContactByNumberFromApiBuffered(phoneNumber)
    }
  }

  getContacts(phoneNumber: string): ContactModel[] {
    // Oldest-first so the participant's primary contact is stable across
    // page loads and MobX re-derivations. Ties (and missing createdAt) break
    // by id to keep the order deterministic.
    return (
      this.root.contact
        .getByNumber(phoneNumber)
        ?.slice()
        .sort(
          (a, b) =>
            (a.createdAt ?? 0) - (b.createdAt ?? 0) ||
            (a.id < b.id ? -1 : a.id > b.id ? 1 : 0),
        ) ?? []
    )
  }

  getMember(phoneNumber: string): MemberModel | null {
    return (
      this.root.member.collection.list.find(
        (m) =>
          m.directNumber?.number === phoneNumber ||
          m.phoneNumbers.find((p) => p.number === phoneNumber),
      ) ?? null
    )
  }

  getContactSuggestion(participant: ParticipantModel): ContactSuggestionModel | null {
    if (participant.member) {
      return null
    }

    if (participant.contact) {
      return null
    }

    return this.root.contactSuggestion.getByNumber(participant.phoneNumber)
  }

  getOpenPhoneNumber(phoneNumber: string): PhoneNumberModel | null {
    return this.root.phoneNumber.byNumber[phoneNumber] ?? null
  }

  getOrCreate(phoneNumber: string): ParticipantModel {
    const participant = this.get(phoneNumber)

    if (participant) {
      return participant
    }

    const newParticipant = new ParticipantModel(this, phoneNumber)

    this.collection.put(newParticipant)

    return newParticipant
  }
}
